import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Modal from 'antd/lib/modal'
import AcknowledgeForm from './AcknowledgeForm'
import TenantEstimatedEmployees from './EstimatedEmployees'
import { getLatestAcknowledgements } from '../Reports/Acknowledgement/Utils'
import Overlay from '../../Overlay'
import { useHistory, useLocation } from 'react-router-dom'

const TenantConfigurator = ({ acknowledgement, population, loading, onRefresh, hideForTerms, hideForUserConfig, onVisibilityChange }) => {
    const [showAcknowledgement, setShowAcknowledgement] = useState(false)
    const [showPopulation, setShowPopulation] = useState(false)
    const user = useSelector((state) => state.app.user)
    const building = useSelector((state) => state.app.building)
    const tenant = useSelector((state) => state.app.tenant)
    const history = useHistory()
    const location = useLocation()

    // Check if we're on a page where the tenant configurator should be shown
    // The tenant configurator should only appear on the dashboard, root page, training portal, or training program pages
    const isOnDashboardOrRoot = location.pathname === '/' ||
        location.pathname === '/dashboard' ||
        location.pathname === '/trainingportal' ||
        location.pathname.startsWith('/trainingprogram')

    // Hide if Terms of Use is visible (to prevent background overlap)
    // Otherwise, allow showing acknowledgement only when user config is not visible,
    // but allow population modal to show immediately after acknowledgement (internal sequencing)
    // FIXED: Also check if we're on the correct page - tenant configurator should only show on dashboard
    if (hideForTerms) {
        var shouldBeVisible = false;
    } else {
        var shouldBeVisible = isOnDashboardOrRoot && ((showAcknowledgement && !hideForUserConfig) || showPopulation);
    }

    // Notify parent when visibility changes
    useEffect(() => {
        if (onVisibilityChange) {
            onVisibilityChange(shouldBeVisible);
        }
    }, [shouldBeVisible, onVisibilityChange]);

    useEffect(() => {
        if (!loading) {
            // Check if user is a tenant admin
            const isTenantAdmin = user.roles?.includes('TENANT_ADMIN')

            // For tenant admins, the activation policy should show regardless of other setup completion
            // For non-tenant admins, don't show anything (they shouldn't see this modal at all)
            if (!isTenantAdmin) {
                setShowAcknowledgement(false)
                setShowPopulation(false)
                return
            }

            // For tenant admins, only check basic user existence (not full configuration completion)
            // This prevents the modal from showing during the very initial login process
            // but allows it to show once the user object is populated
            if (!user.pk || !user.firstName || !user.lastName) {
                setShowAcknowledgement(false)
                setShowPopulation(false)
                return
            }

            // Remove Terms of Use coordination logic - let z-index handle layering
            // This prevents issues with Redux state updates and timing

            const latestAck = getLatestAcknowledgements(acknowledgement)

            // Determine if we should show the acknowledgment form
            let shouldShowAcknowledgement = false

            if (!latestAck) {
                // No acknowledgments exist, show the form
                shouldShowAcknowledgement = true
            } else if (latestAck.declined) {
                // Latest acknowledgment was declined, show the form
                shouldShowAcknowledgement = true
            } else if (!latestAck.active) {
                // Latest acknowledgment is not active, show the form
                shouldShowAcknowledgement = true
            } else {
                // Latest acknowledgment is active (accepted)

                // If the latest acknowledgment was accepted by this user, don't show the form
                if (latestAck.acknowledgedBy?.pk === user.pk) {
                    shouldShowAcknowledgement = false
                } else {
                    // Latest acknowledgment was accepted by someone else
                    // Check if this user has declined before
                    if (acknowledgement && acknowledgement.acknowledgements) {
                        const userDeclinedAcks = acknowledgement.acknowledgements.filter(ack =>
                            ack.acknowledgedBy?.pk === user.pk && ack.declined
                        )

                        if (userDeclinedAcks.length > 0) {
                            // This user has declined before, check if someone else accepted after the decline
                            const userLatestDecline = userDeclinedAcks.reduce((latest, current) =>
                                (!latest.timestamp || current.timestamp > latest.timestamp) ? current : latest
                            )

                            // If the latest overall acknowledgment is accepted and happened after this user's decline
                            if (latestAck.active && !latestAck.declined && latestAck.timestamp > userLatestDecline.timestamp) {
                                // Another admin accepted after this user declined, don't show the form
                                shouldShowAcknowledgement = false
                            } else {
                                // This user declined and no one else has accepted since, show the form
                                shouldShowAcknowledgement = true
                            }
                        } else {
                            // This user hasn't declined, but someone else has accepted
                            // Don't show the form since the policy is already accepted
                            shouldShowAcknowledgement = false
                        }
                    } else {
                        // No acknowledgment data, but latestAck exists - should not happen, but be safe
                        shouldShowAcknowledgement = false
                    }
                }
            }

            setShowAcknowledgement(shouldShowAcknowledgement)

            // Show population form if:
            // 1. No population data exists, OR
            // 2. Population estimated is 0 (needs to be set)
            // AND we're not currently showing the acknowledgement form
            // FIXED: Simplified logic to handle both existing and new user cases
            const shouldShowPopulation = (!population || population.estimated === 0) && !shouldShowAcknowledgement;

            if (shouldShowPopulation) {
                setShowPopulation(true)
            } else {
                setShowPopulation(false)
            }
        }
    }, [acknowledgement, population, loading, user.pk, user.firstName, user.lastName, user.roles, building])

    const onAcknowledged = () => {
        setShowAcknowledgement(false)

        // FIXED: Always check population after acknowledgement, regardless of current population state
        // This ensures the modal shows for users with multiple associations
        if (!population || population.estimated === 0) {
            setShowPopulation(true)
        } else {
            // FIXED: Only navigate to admin dashboard if we're on the dashboard or root page
            // This prevents navigation when the tenant configurator appears on other pages
            if (isOnDashboardOrRoot) {
                setTimeout(() => {
                    history.push(`/admin/tenants/${tenant.pk}`)
                }, 500)
            }
        }
    }

    const onEstimatedPopulationUpdated = (item) => {
        if (item.data) {
            setShowPopulation(false)

            // FIXED: Only navigate to admin dashboard if we're on the dashboard or root page
            // This prevents navigation when the tenant configurator appears on other pages
            if (isOnDashboardOrRoot) {
                setTimeout(() => {
                    history.push(`/admin/tenants/${tenant.pk}`)
                }, 500)
            }
        }
    }

    return (
        <Overlay
            onClose={() => Modal.error({ title: 'Please complete the action to proceed.' })}
            visible={shouldBeVisible}
            headerText={acknowledgement ? 'BSS Tenant Activation Policy' : 'Tenant Setup'}
            closebutton={false}
            zIndex={1000}
        >
            <div style={{ padding: '1.64em', width: 505 }}>
                {showAcknowledgement && <AcknowledgeForm callback={onAcknowledged} onRefresh={onRefresh} />}
                {showPopulation && !showAcknowledgement && <TenantEstimatedEmployees onUpdate={onEstimatedPopulationUpdated} />}
            </div>
        </Overlay>
    )
}

export default TenantConfigurator
